/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_bridge

/**
 * Generic child enumeration and rebuild for [IgnitorDsl] trees, the substrate the graph
 * optimizer walks on.
 *
 * Both functions are EXHAUSTIVE expression-form `when`s over all 82 node types, with NO
 * `else` arm on purpose: a fallback would silently stop descending into any node added later,
 * so optimizations would quietly stop firing under it with nothing failing to compile. Adding a
 * node type must break this file.
 *
 * One more duty rides on adding a node here, stated as the house convention: runtime code must
 * never consume the freq ARGUMENT except to forward it — freq-dependence is expressed as a
 * param defaulting to [IgnitorDsl.Freq], so the D13 fold predicate can see it structurally
 * (`Fm.freq` is the precedent; it used to be a special case in `usesMusicalFreq` and a node
 * that breaks the convention re-creates that bug: a detune above it folds away silently). Since
 * 2026-10-07 the same predicate also decides `MemoizingIgnitor.freqInvariant`, so such a node has
 * a second, worse consequence: when it is shared by readers at two pitches, its memo drops the
 * freq key and hands one reader's freq-dependent samples to the other (a wrong sound).
 * The complete, deliberate exception list (the baseline for the next sweep): `FreqIgnitor`
 * EMITS the argument (it is the leaf), the two detune ignitors MULTIPLY it (that is detune's
 * whole mechanism), and `MemoizingIgnitor` KEYS on it (unless the subtree reads no `Freq`).
 *
 * **Child order is part of the contract** — [withChildNodes] re-reads the list positionally, so
 * the order here must match constructor order for every node, and both functions must agree.
 * `IgnitorDslWalkSpec` pins round-tripping for every type, sections and sine partials included.
 *
 * Naming note: these are `childNodes`/`withChildNodes`, NOT `children`, because
 * [IgnitorDsl.Variants] already has a `children` PROPERTY that a function of the same name
 * would collide with inside its own arm.
 */
fun IgnitorDsl.childNodes(): List<IgnitorDsl> {
    return when (this) {
        is IgnitorDsl.Abs -> listOf(inner)
        is IgnitorDsl.Accelerate -> listOf(inner, semitones)
        is IgnitorDsl.Adsr -> listOf(
            inner, attack, decay, sustain, release, attackCurve, decayCurve, releaseCurve, declick, on,
        )
        is IgnitorDsl.Affine -> listOf(inner, pre, mul, add)
        is IgnitorDsl.Bandpass -> listOf(
            inner, freq, q, analog, env, attack, decay, sustain, release, attackCurve, decayCurve, releaseCurve,
        )
        is IgnitorDsl.BerlinNoise -> listOf(rate, octaves, persistence)
        is IgnitorDsl.BrownNoise -> listOf(leak)
        is IgnitorDsl.Ceil -> listOf(inner)
        is IgnitorDsl.Clamp -> listOf(inner, lo, hi)
        is IgnitorDsl.Coarse -> listOf(inner, factor)
        is IgnitorDsl.Constant -> emptyList()
        is IgnitorDsl.Crackle -> listOf(chaos)
        is IgnitorDsl.Crush -> listOf(inner, bits)
        is IgnitorDsl.Detune -> listOf(inner, semitones)
        is IgnitorDsl.Distort -> listOf(inner, amount, shape)
        is IgnitorDsl.Div -> listOf(left, right)
        is IgnitorDsl.Drive -> listOf(inner, amount)
        is IgnitorDsl.Dust -> listOf(density, tail, bipolar)
        is IgnitorDsl.Eq -> listOf(inner) + sections.flatMap { it.childNodes() }
        is IgnitorDsl.Exp -> listOf(inner)
        is IgnitorDsl.Floor -> listOf(inner)
        is IgnitorDsl.Fm -> listOf(carrier, modulator, ratio, depth, attack, decay, sustain, release, freq)
        is IgnitorDsl.Frac -> listOf(inner)
        is IgnitorDsl.Freq -> emptyList()
        is IgnitorDsl.Highpass -> listOf(
            inner, freq, q, analog, passes, env, attack, decay, sustain, release, attackCurve, decayCurve, releaseCurve,
        )
        is IgnitorDsl.Impulse -> listOf(freq, analog, phase)
        is IgnitorDsl.Lerp -> listOf(left, right, t)
        is IgnitorDsl.Log -> listOf(inner)
        is IgnitorDsl.Lowpass -> listOf(
            inner, freq, q, analog, passes, env, attack, decay, sustain, release, attackCurve, decayCurve, releaseCurve,
        )
        is IgnitorDsl.Max -> listOf(left, right)
        is IgnitorDsl.Min -> listOf(left, right)
        is IgnitorDsl.Minus -> listOf(left, right)
        is IgnitorDsl.Mod -> listOf(left, right)
        is IgnitorDsl.Neg -> listOf(inner)
        is IgnitorDsl.Notch -> listOf(
            inner, freq, q, analog, env, attack, decay, sustain, release, attackCurve, decayCurve, releaseCurve,
        )
        is IgnitorDsl.OnePoleLowpass -> listOf(inner, freq)
        is IgnitorDsl.OptimizerHint -> listOf(inner)
        is IgnitorDsl.Param -> emptyList()
        is IgnitorDsl.PerlinNoise -> listOf(rate, octaves, persistence)
        is IgnitorDsl.Phaser -> listOf(inner, rate, wet, center, sweep, floor)
        is IgnitorDsl.PinkNoise -> emptyList()
        is IgnitorDsl.PitchEnvelope -> listOf(
            inner, semitones, attack, decay, sustain, release, attackCurve, decayCurve, releaseCurve,
        )
        is IgnitorDsl.PitchMod -> listOf(inner, mod)
        is IgnitorDsl.PitchModSemitones -> listOf(inner, mod)
        is IgnitorDsl.Pluck -> listOf(freq, feedback, brightness, pickPosition, stiffness, analog)
        is IgnitorDsl.Plus -> listOf(left, right)
        is IgnitorDsl.Pow -> listOf(base, exp)
        is IgnitorDsl.Pulze -> listOf(freq, duty, analog, phase)
        is IgnitorDsl.Ramp -> listOf(freq, analog, phase)
        is IgnitorDsl.Range -> listOf(inner, from, to)
        is IgnitorDsl.RawPulze -> listOf(freq, duty, analog, phase)
        is IgnitorDsl.Recip -> listOf(inner)
        is IgnitorDsl.Round -> listOf(inner)
        is IgnitorDsl.Sample -> emptyList()
        is IgnitorDsl.Saw -> listOf(freq, analog, phase)
        is IgnitorDsl.Select -> listOf(cond, whenTrue, whenFalse)
        is IgnitorDsl.Shape -> listOf(inner, shape)
        is IgnitorDsl.Shimmer -> listOf(inner, wet, feedback, tone, floor)
        is IgnitorDsl.Sign -> listOf(inner)
        is IgnitorDsl.Silence -> emptyList()
        is IgnitorDsl.Sine -> listOf(
            freq, analog, fundamental, harmonics, harmonicsRolloff, octaves, octavesRolloff,
            suboctaves, suboctavesRolloff, analogSpread, phase,
        ) + partials.flatMap { listOf(it.ratio, it.gain, it.phase) }
        is IgnitorDsl.Sq -> listOf(inner)
        is IgnitorDsl.Sqrt -> listOf(inner)
        is IgnitorDsl.Square -> listOf(freq, analog, phase)
        is IgnitorDsl.SuperPluck -> listOf(
            freq, voices, spread, feedback, brightness, pickPosition, stiffness, analog, analogSpread,
        )
        is IgnitorDsl.SuperRamp -> listOf(freq, voices, spread, analog, analogSpread, phase)
        is IgnitorDsl.SuperSaw -> listOf(freq, voices, spread, analog, analogSpread, phase)
        is IgnitorDsl.SuperSine -> listOf(freq, voices, spread, analog, analogSpread, phase)
        is IgnitorDsl.SuperSquare -> listOf(freq, voices, spread, analog, analogSpread, phase)
        is IgnitorDsl.SuperTri -> listOf(freq, voices, spread, analog, analogSpread, phase)
        is IgnitorDsl.Tanh -> listOf(inner)
        is IgnitorDsl.Times -> listOf(left, right)
        is IgnitorDsl.Tremolo -> listOf(inner, rate, depth, shape, rangeFrom, rangeTo)
        is IgnitorDsl.Tri -> listOf(freq, analog, phase)
        is IgnitorDsl.Variants -> children
        is IgnitorDsl.Parallel -> branches
        is IgnitorDsl.OversampleBy -> listOf(inner, body, factor)
        is IgnitorDsl.OversampleAtLeast -> listOf(inner, body, factor)
        is IgnitorDsl.RegionInput -> emptyList()
        is IgnitorDsl.Vibrato -> listOf(inner, rate, semitones, rangeFrom, rangeTo, phase)
        is IgnitorDsl.WhiteNoise -> listOf(color)
        is IgnitorDsl.Zamp -> listOf(freq, analog, phase)
        is IgnitorDsl.Zawtooth -> listOf(freq, analog, phase)
    }
}

/**
 * Rebuilds this node with [new] in place of [childNodes], positionally.
 *
 * For any node WITH children this returns a new instance even when the children are unchanged;
 * the five childless leaves return `this`. Callers that want to preserve identity for untouched
 * subtrees must check that themselves (the optimizer does, so shared subtrees stay shared and
 * `===` survives a rewrite).
 *
 * @throws IllegalArgumentException on a size mismatch. This is an internal invariant, not user
 * input: a mis-chunked list would silently swap a bell's `q` into its `db`, so it must fail
 * loudly rather than change a sound silently.
 *
 * NOTE this DOES run on the JS audio thread: `IgnitorRegistry.register` is reached from the
 * worklet's `port.onmessage`. `register` wraps the whole optimize call so nothing escapes into
 * that dispatch, but do not add throwing or allocating work here on the assumption that it is
 * off the render path.
 */
fun IgnitorDsl.withChildNodes(new: List<IgnitorDsl>): IgnitorDsl {
    require(new.size == childNodes().size) {
        "withChildNodes size mismatch for ${this::class.simpleName}: expected ${childNodes().size}, got ${new.size}"
    }

    return when (this) {
        is IgnitorDsl.Abs -> copy(inner = new[0])
        is IgnitorDsl.Accelerate -> copy(inner = new[0], semitones = new[1])
        is IgnitorDsl.Adsr -> copy(
            inner = new[0],
            attack = new[1],
            decay = new[2],
            sustain = new[3],
            release = new[4],
            attackCurve = new[5],
            decayCurve = new[6],
            releaseCurve = new[7],
            declick = new[8],
            on = new[9],
        )
        is IgnitorDsl.Affine -> copy(inner = new[0], pre = new[1], mul = new[2], add = new[3])
        is IgnitorDsl.Bandpass -> copy(
            inner = new[0], freq = new[1], q = new[2], analog = new[3],
            env = new[4], attack = new[5], decay = new[6], sustain = new[7], release = new[8],
            attackCurve = new[9], decayCurve = new[10], releaseCurve = new[11],
        )
        is IgnitorDsl.BerlinNoise -> copy(rate = new[0], octaves = new[1], persistence = new[2])
        is IgnitorDsl.BrownNoise -> copy(leak = new[0])
        is IgnitorDsl.Ceil -> copy(inner = new[0])
        is IgnitorDsl.Clamp -> copy(inner = new[0], lo = new[1], hi = new[2])
        is IgnitorDsl.Coarse -> copy(inner = new[0], factor = new[1])
        is IgnitorDsl.Constant -> this
        is IgnitorDsl.Crackle -> copy(chaos = new[0])
        is IgnitorDsl.Crush -> copy(inner = new[0], bits = new[1])
        is IgnitorDsl.Detune -> copy(inner = new[0], semitones = new[1])
        is IgnitorDsl.Distort -> copy(inner = new[0], amount = new[1], shape = new[2])
        is IgnitorDsl.Div -> copy(left = new[0], right = new[1])
        is IgnitorDsl.Drive -> copy(inner = new[0], amount = new[1])
        is IgnitorDsl.Dust -> copy(density = new[0], tail = new[1], bipolar = new[2])
        is IgnitorDsl.Eq -> {
            var next = 1
            copy(
                inner = new[0],
                sections = sections.map { section ->
                    val take = section.childNodes().size
                    section.withChildNodes(new.subList(next, next + take)).also { next += take }
                },
            )
        }
        is IgnitorDsl.Exp -> copy(inner = new[0])
        is IgnitorDsl.Floor -> copy(inner = new[0])
        is IgnitorDsl.Fm -> copy(
            carrier = new[0],
            modulator = new[1],
            ratio = new[2],
            depth = new[3],
            attack = new[4],
            decay = new[5],
            sustain = new[6],
            release = new[7],
            freq = new[8],
        )
        is IgnitorDsl.Frac -> copy(inner = new[0])
        is IgnitorDsl.Freq -> this
        is IgnitorDsl.Highpass -> copy(
            inner = new[0], freq = new[1], q = new[2], analog = new[3], passes = new[4],
            env = new[5], attack = new[6], decay = new[7], sustain = new[8], release = new[9],
            attackCurve = new[10], decayCurve = new[11], releaseCurve = new[12],
        )
        is IgnitorDsl.Impulse -> copy(freq = new[0], analog = new[1], phase = new[2])
        is IgnitorDsl.Lerp -> copy(left = new[0], right = new[1], t = new[2])
        is IgnitorDsl.Log -> copy(inner = new[0])
        is IgnitorDsl.Lowpass -> copy(
            inner = new[0], freq = new[1], q = new[2], analog = new[3], passes = new[4],
            env = new[5], attack = new[6], decay = new[7], sustain = new[8], release = new[9],
            attackCurve = new[10], decayCurve = new[11], releaseCurve = new[12],
        )
        is IgnitorDsl.Max -> copy(left = new[0], right = new[1])
        is IgnitorDsl.Min -> copy(left = new[0], right = new[1])
        is IgnitorDsl.Minus -> copy(left = new[0], right = new[1])
        is IgnitorDsl.Mod -> copy(left = new[0], right = new[1])
        is IgnitorDsl.Neg -> copy(inner = new[0])
        is IgnitorDsl.Notch -> copy(
            inner = new[0], freq = new[1], q = new[2], analog = new[3],
            env = new[4], attack = new[5], decay = new[6], sustain = new[7], release = new[8],
            attackCurve = new[9], decayCurve = new[10], releaseCurve = new[11],
        )
        is IgnitorDsl.OnePoleLowpass -> copy(inner = new[0], freq = new[1])
        is IgnitorDsl.OptimizerHint -> copy(inner = new[0])
        is IgnitorDsl.Param -> this
        is IgnitorDsl.PerlinNoise -> copy(rate = new[0], octaves = new[1], persistence = new[2])
        is IgnitorDsl.Phaser -> copy(inner = new[0], rate = new[1], wet = new[2], center = new[3], sweep = new[4], floor = new[5])
        is IgnitorDsl.PinkNoise -> this
        is IgnitorDsl.PitchEnvelope -> copy(
            inner = new[0],
            semitones = new[1],
            attack = new[2],
            decay = new[3],
            sustain = new[4],
            release = new[5],
            attackCurve = new[6],
            decayCurve = new[7],
            releaseCurve = new[8],
        )
        is IgnitorDsl.PitchMod -> copy(inner = new[0], mod = new[1])
        is IgnitorDsl.PitchModSemitones -> copy(inner = new[0], mod = new[1])
        is IgnitorDsl.Pluck -> copy(
            freq = new[0],
            feedback = new[1],
            brightness = new[2],
            pickPosition = new[3],
            stiffness = new[4],
            analog = new[5],
        )
        is IgnitorDsl.Plus -> copy(left = new[0], right = new[1])
        is IgnitorDsl.Pow -> copy(base = new[0], exp = new[1])
        is IgnitorDsl.Pulze -> copy(freq = new[0], duty = new[1], analog = new[2], phase = new[3])
        is IgnitorDsl.Ramp -> copy(freq = new[0], analog = new[1], phase = new[2])
        is IgnitorDsl.Range -> copy(inner = new[0], from = new[1], to = new[2])
        is IgnitorDsl.RawPulze -> copy(freq = new[0], duty = new[1], analog = new[2], phase = new[3])
        is IgnitorDsl.Recip -> copy(inner = new[0])
        is IgnitorDsl.Round -> copy(inner = new[0])
        is IgnitorDsl.Sample -> this
        is IgnitorDsl.Saw -> copy(freq = new[0], analog = new[1], phase = new[2])
        is IgnitorDsl.Select -> copy(cond = new[0], whenTrue = new[1], whenFalse = new[2])
        is IgnitorDsl.Shape -> copy(inner = new[0], shape = new[1])
        is IgnitorDsl.Shimmer -> copy(inner = new[0], wet = new[1], feedback = new[2], tone = new[3], floor = new[4])
        is IgnitorDsl.Sign -> copy(inner = new[0])
        is IgnitorDsl.Silence -> this
        is IgnitorDsl.Sine -> copy(
            freq = new[0], analog = new[1], fundamental = new[2], harmonics = new[3], harmonicsRolloff = new[4],
            octaves = new[5], octavesRolloff = new[6], suboctaves = new[7], suboctavesRolloff = new[8],
            analogSpread = new[9], phase = new[10],
            // three children per partial, in list order, after the eleven fields
            partials = partials.mapIndexed { i, p ->
                val at = 11 + 3 * i

                p.copy(ratio = new[at], gain = new[at + 1], phase = new[at + 2])
            },
        )
        is IgnitorDsl.Sq -> copy(inner = new[0])
        is IgnitorDsl.Sqrt -> copy(inner = new[0])
        is IgnitorDsl.Square -> copy(freq = new[0], analog = new[1], phase = new[2])
        is IgnitorDsl.SuperPluck -> copy(
            freq = new[0],
            voices = new[1],
            spread = new[2],
            feedback = new[3],
            brightness = new[4],
            pickPosition = new[5],
            stiffness = new[6],
            analog = new[7],
            analogSpread = new[8],
        )
        is IgnitorDsl.SuperRamp -> copy(
            freq = new[0], voices = new[1], spread = new[2], analog = new[3], analogSpread = new[4], phase = new[5],
        )
        is IgnitorDsl.SuperSaw -> copy(
            freq = new[0], voices = new[1], spread = new[2], analog = new[3], analogSpread = new[4], phase = new[5],
        )
        is IgnitorDsl.SuperSine -> copy(
            freq = new[0], voices = new[1], spread = new[2], analog = new[3], analogSpread = new[4], phase = new[5],
        )
        is IgnitorDsl.SuperSquare -> copy(
            freq = new[0], voices = new[1], spread = new[2], analog = new[3], analogSpread = new[4], phase = new[5],
        )
        is IgnitorDsl.SuperTri -> copy(
            freq = new[0], voices = new[1], spread = new[2], analog = new[3], analogSpread = new[4], phase = new[5],
        )
        is IgnitorDsl.Tanh -> copy(inner = new[0])
        is IgnitorDsl.Times -> copy(left = new[0], right = new[1])
        is IgnitorDsl.Tremolo -> copy(
            inner = new[0], rate = new[1], depth = new[2], shape = new[3], rangeFrom = new[4], rangeTo = new[5],
        )
        is IgnitorDsl.Tri -> copy(freq = new[0], analog = new[1], phase = new[2])
        is IgnitorDsl.Variants -> copy(children = new)
        is IgnitorDsl.Parallel -> copy(branches = new)
        is IgnitorDsl.OversampleBy -> copy(inner = new[0], body = new[1], factor = new[2])
        is IgnitorDsl.OversampleAtLeast -> copy(inner = new[0], body = new[1], factor = new[2])
        is IgnitorDsl.RegionInput -> this
        is IgnitorDsl.Vibrato -> copy(inner = new[0], rate = new[1], semitones = new[2], rangeFrom = new[3], rangeTo = new[4], phase = new[5])
        is IgnitorDsl.WhiteNoise -> copy(color = new[0])
        is IgnitorDsl.Zamp -> copy(freq = new[0], analog = new[1], phase = new[2])
        is IgnitorDsl.Zawtooth -> copy(freq = new[0], analog = new[1], phase = new[2])
    }
}

/** Child enumeration for one EQ section. See [childNodes] for the ordering contract. */
fun IgnitorDsl.EqSection.childNodes(): List<IgnitorDsl> {
    return when (this) {
        is IgnitorDsl.EqSection.Bandpass -> listOf(freq, q)
        is IgnitorDsl.EqSection.Bell -> listOf(freq, q, db)
        is IgnitorDsl.EqSection.Highpass -> listOf(freq, q)
        is IgnitorDsl.EqSection.Lowpass -> listOf(freq, q)
        is IgnitorDsl.EqSection.Notch -> listOf(freq, q)
        is IgnitorDsl.EqSection.RawTap -> listOf(freq, q, gain)
    }
}

/** Rebuilds one EQ section with [new] in place of [childNodes], positionally. */
fun IgnitorDsl.EqSection.withChildNodes(new: List<IgnitorDsl>): IgnitorDsl.EqSection {
    require(new.size == childNodes().size) {
        "withChildNodes size mismatch for ${this::class.simpleName}: expected ${childNodes().size}, got ${new.size}"
    }

    return when (this) {
        is IgnitorDsl.EqSection.Bandpass -> copy(freq = new[0], q = new[1])
        is IgnitorDsl.EqSection.Bell -> copy(freq = new[0], q = new[1], db = new[2])
        is IgnitorDsl.EqSection.Highpass -> copy(freq = new[0], q = new[1])
        is IgnitorDsl.EqSection.Lowpass -> copy(freq = new[0], q = new[1])
        is IgnitorDsl.EqSection.Notch -> copy(freq = new[0], q = new[1])
        is IgnitorDsl.EqSection.RawTap -> copy(freq = new[0], q = new[1], gain = new[2])
    }
}
